import Layout from '@/components/layout/Layout';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Single configurable Suspense skeleton.
 *
 * Variants:
 * - "grid"    — hero + 3-column card grid (default for content pages)
 * - "form"    — hero + 2-column form/sidebar layout (e.g. /contact)
 * - "explore" — hero + sticky toggle bar + 2-column gallery (e.g. /explore)
 *
 * Use `variantForPath(pathname)` to pick the right shape automatically when
 * the route is known at fallback time.
 */
export type SkeletonVariant = 'grid' | 'form' | 'explore';

const FORM_PATHS = new Set<string>(['/contact']);
const EXPLORE_PATHS = new Set<string>(['/explore']);

export function variantForPath(pathname: string | undefined | null): SkeletonVariant {
  if (!pathname) return 'grid';
  if (FORM_PATHS.has(pathname)) return 'form';
  if (EXPLORE_PATHS.has(pathname)) return 'explore';
  // Prefix-aware fallbacks for nested routes within those sections.
  for (const p of FORM_PATHS) if (pathname.startsWith(`${p}/`)) return 'form';
  for (const p of EXPLORE_PATHS) if (pathname.startsWith(`${p}/`)) return 'explore';
  return 'grid';
}

const SR_LABEL: Record<SkeletonVariant, string> = {
  grid: 'Loading page content…',
  form: 'Loading form…',
  explore: 'Loading museum content…',
};

const Hero = ({ titleWidth = 'w-3/4', subLines = 2 }: { titleWidth?: string; subLines?: number }) => (
  <section className="py-20 lg:py-28 bg-gradient-to-b from-secondary/30 to-background">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center space-y-4">
        <Skeleton className="h-3 w-28 mx-auto" />
        <Skeleton className={`h-12 sm:h-14 mx-auto ${titleWidth}`} />
        <div className="divider-brass" />
        {Array.from({ length: subLines }).map((_, i) => (
          <Skeleton
            key={i}
            className={
              i === 0
                ? 'h-4 w-full max-w-xl mx-auto'
                : 'h-4 w-5/6 max-w-lg mx-auto'
            }
          />
        ))}
      </div>
    </div>
  </section>
);

const GridBody = () => (
  <section className="py-16 lg:py-24 bg-background">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="bg-card border border-border overflow-hidden"
            aria-hidden="true"
          >
            <Skeleton className="h-48 w-full rounded-none" />
            <div className="p-5 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FormBody = () => (
  <section className="py-16 lg:py-24 bg-background">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-6" aria-hidden="true">
          <Skeleton className="h-7 w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-32 w-full" />
          </div>
          <Skeleton className="h-11 w-40" />
        </div>

        <div className="lg:col-span-2 space-y-6" aria-hidden="true">
          <Skeleton className="h-7 w-48" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton className="h-10 w-10 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          ))}
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    </div>
  </section>
);

const ExploreBody = () => (
  <>
    <section className="py-8 bg-card border-y border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Skeleton className="h-[78px] w-full" />
          <Skeleton className="h-[78px] w-full" />
        </div>
      </div>
    </section>

    <section className="py-6 bg-card border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-28" />
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-card border border-border overflow-hidden"
              aria-hidden="true"
            >
              <Skeleton className="h-56 w-full rounded-none" />
              <div className="p-6 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

interface RouteSkeletonProps {
  /** Explicit variant. Wins over `pathname` when both are provided. */
  variant?: SkeletonVariant;
  /** When provided and `variant` is omitted, the variant is inferred. */
  pathname?: string;
}

const RouteSkeleton = ({ variant, pathname }: RouteSkeletonProps = {}) => {
  const resolved: SkeletonVariant = variant ?? variantForPath(pathname);

  return (
    <Layout>
      {resolved === 'form' ? (
        <Hero titleWidth="w-1/2" subLines={1} />
      ) : (
        <Hero titleWidth="w-3/4" subLines={2} />
      )}

      {resolved === 'form' && <FormBody />}
      {resolved === 'grid' && <GridBody />}
      {resolved === 'explore' && <ExploreBody />}

      <span className="sr-only" role="status" aria-live="polite">
        {SR_LABEL[resolved]}
      </span>
    </Layout>
  );
};

export default RouteSkeleton;